import AppOptions from "../app/options";
import VideoUrlModel from "../model/video_url";
import EpgHlsUrlModel from "../model/epg_hls_url";
import Html from "./html";
import MuxStatistics from "./mux_statistics";
import { isSafary } from "./util";
import VideoPlaybackOptions from "./video_playback_options";

// What the user can seek in, in timeline seconds
// VOD: 0 -> duration. Live: a window that moves forward, end is the live edge.
export interface PlaybackTimeline {
    start: number;
    end: number;
    // Origin (timelineOrigin) is used to shift timeline for live EPG playback. For example if user is watching for 2 minutes,
    // the time at the furthermost left of the timeline is shown as 2:00 instead of 0:00 and
    // 27:00 instead of 25:00 at the furthermost right. 25 minutes is the seekable range of our live EPG playback.
    // For live streams the origin is the stream start.
    origin: number;
}

class ShakaPlayer {
    private fpLicenseServerUrl: string = '';
    private fpCertificateUrl: string = ''
    private wvLicenseServerUrl: string = '';

    private fairplayAssetId = '';
    private fpCertificate: Uint8Array | null = null;

    private player: any = null;

    private loading: Promise<void> | null = null;

    private timelineOrigin: number = 0;

    constructor(
        protected html: Html,
        protected muxStats: MuxStatistics,
        protected options: AppOptions
    ) {
        const fpLicenseUrl = this.options.fpLicenseServerUrl;
        this.fpLicenseServerUrl = fpLicenseUrl ? fpLicenseUrl + '/fps/rest/getLicense' : '';
        this.fpCertificateUrl = fpLicenseUrl ? fpLicenseUrl + '/fps-pub.der' : '';
        this.wvLicenseServerUrl = this.options.wvLicenseServerUrl || '';
    }

    load(): Promise<void> {
        if (this.loading) {
            return this.loading;
        }

        this.loading = this.html.loadScript('/assets/shaka/shaka-player.compiled.js')
            .then(() => {
                if (typeof shaka === 'undefined') {
                    throw new Error('Shaka is undefined.');
                }

                this.installPolyfills();

                if (!shaka.Player.isBrowserSupported()) {
                    console.error('Shaka player is not supported by this browser');
                }
            })
            .catch(err => {
                this.loading = null;
                throw err;
            });

        return this.loading;
    }

    timeline(): PlaybackTimeline | null {
        const range = this.seekRange();
        if (!range) {
            return null;
        }

        return {
            start: range.start - this.timelineOrigin,
            end: range.end - this.timelineOrigin,
            origin: this.timelineOrigin
        };
    }

    async initPlayer(video: HTMLMediaElement, videoUrl: VideoUrlModel | EpgHlsUrlModel, playbackOptions: VideoPlaybackOptions): Promise<any> {
        await this.destroy();

        this.player = new shaka.Player();
        await this.player.attach(video);

        this.muxStats.monitor(video, playbackOptions, this.player);

        this.player.addEventListener('error', (event: any) => this.onError(event.detail));

        this.player.addEventListener('buffering', (event: any) => this.html.toggleClassIf('body', event.buffering, 'buffering'));

        // Shaka's default failure callback retries failed segment downloads only on live streams.
        // On VOD one segment timeout stopped streaming for good and the picture froze until the user seeked.
        this.player.configure('streaming.failureCallback', (error: any) => this.onStreamingFailure(error));

        if (this.isDrm(videoUrl)) {
            // Only sites with a FairPlay license server (web) can use FairPlay. isSafary() alone is not
            // enough, because every WebKit browser (also some TVs) reports an Apple vendor.
            if (this.fpLicenseServerUrl && isSafary()) {
                await this.configureFairplay(videoUrl);
            } else {
                this.configureWidevine(videoUrl);
            }
        }

        // Some configuration might differ if shaka is playing in browser or on tv.
        if (this.options.device.family === 'Browser') {
            // This is fix for Audio-Video correct synchronisation. Web only, tvapp must keep it off.
            this.player.configure('manifest.hls.sequenceMode', true);

            // This is fix 2 if code above does not sync them correctly. Update: it seems that the above
            // fix works ok, and we're leaving this code below just in case we might need it.
            // this.player.configure('manifest.hls.ignoreManifestTimestampsInSegmentsMode', true);
        }
        // BG source videos have missaligned chunks and keyframes which causes skipping on shaka because it does not internally
        // solve these problems... while we wait for BG to fix its videos we resort to native play which tones down the issue.
        // On browser this is fixed via sequenceMode which sadly does not work on tvs.
        if (this.options.device.family !== 'Browser' && this.options.country === 'bg' && playbackOptions.type === 'video') {
            this.player.configure('streaming.preferNativeHls', true);
        }

        // Shaka's own live seek window drops segments the packager still serves, so we use the
        // packager's DVR window (3h in every country).
        if (playbackOptions.type === 'live_stream') {
            this.player.configure('manifest.availabilityWindowOverride', 3 * 60 * 60);
        }

        try {
            await this.player.load(videoUrl.url, playbackOptions.startAt || undefined);
        } catch (err) {
            await this.destroy();
            throw err;
        }

        this.setTimelineOrigin(playbackOptions);

        return this.player;
    }

    async destroy(): Promise<void> {
        this.timelineOrigin = 0;

        if (this.player) {
            const player = this.player;
            this.player = null;
            player.mux?.destroy();
            await player.destroy();
        }

        this.html.removeClass('body', 'buffering');
    }

    private seekRange(): { start: number, end: number } | null {
        if (!this.player) {
            return null;
        }

        const range = this.player.seekRange();
        if (!range || !isFinite(range.end) || range.end <= range.start) {
            return null;
        }

        return { start: range.start, end: range.end };
    }

    private setTimelineOrigin(playbackOptions: VideoPlaybackOptions): void {
        const range = this.seekRange();
        if (!range) {
            this.timelineOrigin = 0;
            return;
        }

        if (playbackOptions.type === 'live_stream') {
            // Live streams count from StartAt, also when it's already out of the 3h DVR window.
            const streamStart = this.liveStreamStart(playbackOptions);
            this.timelineOrigin = streamStart !== null ? Math.min(streamStart, range.start) : range.start;
            return;
        }

        this.timelineOrigin = range.start;
    }

    // StartAt in player time.
    private liveStreamStart(playbackOptions: VideoPlaybackOptions): number | null {
        if (!playbackOptions.streamStartAt) {
            return null;
        }

        const presentationStart = this.player.getPresentationStartTimeAsDate();
        if (!presentationStart) {
            return null;
        }

        return playbackOptions.streamStartAt - presentationStart.getTime() / 1000;
    }

    /**
     * tvapp only: hiding navigator.mediaCapabilities makes Shaka's device check always install
     * its own polyfill. It is a fix for the A1 STB, where playback doesn't work with native
     * capabilities.
     */
    private installPolyfills(): void {
        if (this.options.device.family !== 'Browser') {
            try {
                Object.defineProperty(navigator, 'mediaCapabilities', {
                    value: undefined,
                    writable: true,
                    configurable: true,
                });
            } catch { /* Hiding mediaCapabilities failed, so playback might not work on the A1 STB. */ }
        }

        shaka.polyfill.installAll();
    }

    private isDrm(videoUrl: VideoUrlModel | EpgHlsUrlModel): boolean {
        return !!videoUrl.license;
    }

    private isLicense(type: any) {
        return type === shaka.net.NetworkingEngine.RequestType.LICENSE;
    }

    private configureWidevine(videoUrl: VideoUrlModel | EpgHlsUrlModel): void {
        this.player.configure({
            drm: {
                servers: {
                    'com.widevine.alpha': this.wvLicenseServerUrl
                },
                advanced: {
                    'com.widevine.alpha': {
                        'headers': {
                            'X-Drm-Message': videoUrl.url
                        }
                    }
                }
            }
        });
    }

    private async loadFpCertificate(): Promise<Uint8Array | null> {
        if (this.fpCertificate) {
            return this.fpCertificate;
        }

        try {
            const res = await fetch(this.fpCertificateUrl);
            if (!res.ok) {
                throw new Error('HTTP ' + res.status);
            }
            this.fpCertificate = new Uint8Array(await res.arrayBuffer());
            return this.fpCertificate;
        } catch (err) {
            console.warn('FairPlay certificate load failed', err);
            return null;
        }
    }

    private async configureFairplay(videoUrl: VideoUrlModel | EpgHlsUrlModel): Promise<void> {
        const certificate = await this.loadFpCertificate();

        this.player.configure({
            drm: {
                servers: {
                    'com.apple.fps': this.fpLicenseServerUrl,
                    'com.apple.fps.1_0': this.fpLicenseServerUrl
                },
                advanced: {
                    'com.apple.fps': {
                        serverCertificate: certificate || undefined,
                        serverCertificateUri: this.fpCertificateUrl
                    },
                    'com.apple.fps.1_0': {
                        serverCertificate: certificate || undefined,
                        serverCertificateUri: this.fpCertificateUrl
                    }
                },
                initDataTransform: this.fairplayInitDataTransform.bind(this),
            }
        });

        const netEngine = this.player.getNetworkingEngine();
        netEngine.registerRequestFilter((type: any, request: any) => this.fairplayRequestFilter(type, request, videoUrl));
        netEngine.registerResponseFilter((type: any, response: any) => this.fairplayResponseFilter(type, response));
    }

    private fairplayRequestFilter(type: any, request: any, videoUrl: VideoUrlModel | EpgHlsUrlModel): void {
        if (!this.isLicense(type)) {
            return;
        }

        const spc = shaka.util.Uint8ArrayUtils.toStandardBase64(new Uint8Array(request.body));
        const assetId = encodeURIComponent(this.resolveFairplayAssetId(request));

        request.headers['Content-Type'] = 'application/json';
        request.headers['Authorization'] = videoUrl.license;
        request.body = shaka.util.StringUtils.toUTF8(JSON.stringify({ spc, assetId }));
    }

    private fairplayResponseFilter(type: any, response: any): void {
        if (!this.isLicense(type)) {
            return;
        }

        let text = shaka.util.StringUtils.fromUTF8(response.data).trim();

        if (text.slice(0, 5) === '<ckc>' && text.slice(-6) === '</ckc>') {
            text = text.slice(5, -6);
        } else if (text.startsWith('{')) {
            try {
                const parsed = JSON.parse(text);
                text = parsed.ckc || parsed.Ckc || parsed.license || text;
            } catch { /* keep text as-is */ }
        }

        response.data = shaka.util.Uint8ArrayUtils.fromBase64(text).buffer;
    }

    private fairplayInitDataTransform(initData: Uint8Array, initDataType: string, drmInfo: any): Uint8Array {
        if (initDataType !== 'skd') {
            return initData;
        }

        try {
            this.fairplayAssetId = shaka.drm.FairPlay.defaultGetContentId(initData) || this.fairplayAssetId;
        } catch { /* keep previous asset id */ }

        // Same check Shaka's own default initDataTransform uses internally.
        const usingApplePolyfill = (window as any).shakaMediaKeysPolyfill === 'apple';
        if (!usingApplePolyfill) {
            return initData;
        }

        const cert = drmInfo && drmInfo.serverCertificate;
        if (!cert || !cert.byteLength) {
            throw new Error('FairPlay server certificate required before initDataTransform');
        }

        return shaka.drm.FairPlay.initDataTransform(initData, this.fairplayAssetId, cert);
    }

    private resolveFairplayAssetId(request: any): string {
        try {
            if (request.initData && request.initData.byteLength) {
                const fromSkd = shaka.drm.FairPlay.defaultGetContentId(request.initData);
                if (fromSkd) {
                    return fromSkd;
                }
            }
        } catch { /* fall through */ }

        return this.fairplayAssetId;
    }

    private onStreamingFailure(error: any): void {
        const Code = shaka.util.Error.Code;
        const networkErrors = [Code.BAD_HTTP_STATUS, Code.HTTP_ERROR, Code.TIMEOUT];

        if (!this.player || !networkErrors.includes(error.code)) {
            return;
        }

        error.severity = shaka.util.Error.Severity.RECOVERABLE;
        this.player.retryStreaming(1);
    }

    private onError(error: any) {
        // Native error is caught by video onerror, do nothing here
        if (error instanceof Error) {
            return;
        }

        if (error.severity === shaka.util.Error.Severity.CRITICAL) {
            console.error(`Player error - ${error.category} - ${error.code}`);
        }
    }
}

export default ShakaPlayer;
